import { useEffect, useState, useCallback, useRef } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, fmtDateTime } from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { RichEditor, SafeHtml, isEmptyHtml } from "@/components/RichEditor";
import { MultiSelect } from "@/components/MultiSelect";
import { StatoBadge } from "@/pages/KnowledgeBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CaretRightIcon as CaretRight, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, PaperclipIcon as Paperclip, EyeIcon as Eye, DownloadSimpleIcon as DownloadSimple, X, FloppyDiskIcon as FloppyDisk, PaperPlaneTiltIcon as PaperPlaneTilt, ArchiveIcon as Archive, ClockCounterClockwiseIcon as ClockCounterClockwise } from "@phosphor-icons/react";

export default function KBArticle() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const editMode = sp.get("edit") === "1";
  const [art, setArt] = useState(null);
  const [cats, setCats] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const creatingRef = useRef(false);

  const loadArticle = useCallback(async (aid) => {
    const { data } = await api.get(`/kb/articles/${aid}`);
    setArt(data);
    setForm({
      titolo: data.titolo || "", cartella_id: data.cartella_id, categoria_id: data.categoria_id,
      contenuto_html: data.contenuto_html || "", stato: data.stato,
      visibile_area_cliente: !!data.visibile_area_cliente, clienti_visibili_ids: data.clienti_visibili_ids || [],
      tags: (data.tags || []).join(", "), seo_title: data.seo_title || "", seo_description: data.seo_description || "",
    });
  }, []);

  useEffect(() => {
    api.get("/kb/categories").then(({ data }) => setCats(data || [])).catch(() => {});
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (id === "new") {
      if (creatingRef.current) return;
      creatingRef.current = true;
      const folder = sp.get("folder");
      api.post("/kb/articles", { titolo: "Senza titolo", cartella_id: folder, contenuto_html: "", stato: "bozza" })
        .then(({ data }) => nav(`/knowledge-base/article/${data.id}?edit=1`, { replace: true }))
        .catch((e) => { toast.error(formatApiErrorDetail(e)); nav("/knowledge-base"); });
      return;
    }
    loadArticle(id).catch(() => { toast.error("Articolo non trovato"); nav("/knowledge-base"); });
  }, [id]); // eslint-disable-line

  const folderOptions = (cats.find((c) => c.id === form?.categoria_id)?.folders) || [];
  const clientOptions = clients.map((c) => ({ value: c.id, label: c.ragione_sociale }));

  const save = async (extra = {}) => {
    const payload = {
      titolo: form.titolo.trim() || "Senza titolo", cartella_id: form.cartella_id,
      contenuto_html: form.contenuto_html, stato: form.stato,
      visibile_area_cliente: form.visibile_area_cliente, clienti_visibili_ids: form.clienti_visibili_ids,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      seo_title: form.seo_title, seo_description: form.seo_description, ...extra,
    };
    try {
      const { data } = await api.put(`/kb/articles/${id}`, payload);
      setArt(data); toast.success("Articolo salvato");
      setSp({}); // esci da edit
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const publish = async () => { try { const { data } = await api.post(`/kb/articles/${id}/publish`); setArt(data); setForm((f) => ({ ...f, stato: "pubblicato" })); toast.success("Pubblicato"); } catch (e) { toast.error(formatApiErrorDetail(e)); } };
  const archive = async () => { try { const { data } = await api.post(`/kb/articles/${id}/archive`); setArt(data); setForm((f) => ({ ...f, stato: "archiviato" })); toast.success("Archiviato"); } catch (e) { toast.error(formatApiErrorDetail(e)); } };
  const del = async () => { if (!window.confirm("Eliminare questo articolo?")) return; try { await api.delete(`/kb/articles/${id}`); toast.success("Articolo eliminato"); nav(art?.cartella_id ? `/knowledge-base/folder/${art.cartella_id}` : "/knowledge-base"); } catch (e) { toast.error(formatApiErrorDetail(e)); } };

  const uploadInlineImage = useCallback(async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post(`/kb/articles/${id}/inline-image`, fd);
    return data.url;
  }, [id]);

  const addAttachments = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/kb/articles/${id}/attachments`, fd);
      }
      await loadArticle(id); toast.success("Allegato caricato");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeAtt = async (aid) => { try { await api.delete(`/kb/articles/${id}/attachments/${aid}`); await loadArticle(id); } catch (e) { toast.error(formatApiErrorDetail(e)); } };
  const previewAtt = async (a) => { try { const res = await api.get(`/kb/articles/${id}/attachments/${a.id}/inline`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([res.data], { type: a.content_type || res.data.type })); window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000); } catch { toast.error("Anteprima non disponibile"); } };
  const downloadAtt = async (a) => { try { const res = await api.get(`/kb/articles/${id}/attachments/${a.id}/download`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([res.data])); const el = document.createElement("a"); el.href = url; el.download = a.filename; document.body.appendChild(el); el.click(); el.remove(); window.URL.revokeObjectURL(url); } catch { toast.error("Errore download"); } };
  const openHistory = async () => { try { const { data } = await api.get(`/kb/articles/${id}/history`); setHistory(data || []); setShowHistory(true); } catch { /* noop */ } };

  if (id === "new" || !art || !form) return <InternalLayout><div className="max-w-[1400px] mx-auto p-4 text-sm text-muted-foreground">Caricamento…</div></InternalLayout>;

  const atts = (art.allegati || []).filter((a) => !a.is_inline);

  return (
    <InternalLayout>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 flex-wrap" data-testid="kb-article-breadcrumb">
          <button onClick={() => nav("/knowledge-base")} className="hover:text-foreground hover:underline">Knowledge Base</button>
          <CaretRight size={12} /><span>{art.categoria?.nome}</span>
          <CaretRight size={12} /><button onClick={() => nav(`/knowledge-base/folder/${art.cartella_id}`)} className="hover:text-foreground hover:underline">{art.cartella?.nome}</button>
          <CaretRight size={12} /><span className="text-foreground font-medium truncate max-w-[280px]">{art.titolo}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">
          <div className="flex-1 min-w-0">
            {!editMode ? (
              <>
                <div className="flex items-center justify-end gap-2 mb-3">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={openHistory} data-testid="kb-history-btn"><ClockCounterClockwise size={15} /> Storico</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => setSp({ edit: "1" })} data-testid="kb-edit-btn"><PencilSimple size={15} /> Modifica</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={del} data-testid="kb-delete-btn"><Trash size={15} /> Elimina</Button>
                </div>
                <div className="h-1 bg-emerald-500 rounded-t-sm" />
                <Card className="rounded-sm rounded-t-none shadow-none p-6">
                  <h1 className="font-heading text-2xl font-bold text-slate-800 mb-3" data-testid="kb-article-title">{art.titolo}</h1>
                  {atts.length > 0 && (
                    <div className="mb-5 p-3 bg-secondary/40 rounded-sm border border-border" data-testid="kb-article-attachments">
                      <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1"><Paperclip size={13} /> Allegati ({atts.length})</p>
                      <div className="space-y-1.5">
                        {atts.map((a) => (
                          <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`kb-att-${a.id}`}>
                            <Paperclip size={14} className="text-muted-foreground shrink-0" />
                            <button onClick={() => previewAtt(a)} className="text-blue-600 hover:underline truncate max-w-[340px]" data-testid={`kb-att-link-${a.id}`}>{a.filename}</button>
                            <span className="text-xs text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB · {a.uploaded_by || "—"} · {fmtDateTime(a.uploaded_at)}</span>
                            <button onClick={() => previewAtt(a)} title="Anteprima" className="text-muted-foreground hover:text-blue-600 ml-auto"><Eye size={15} /></button>
                            <button onClick={() => downloadAtt(a)} title="Scarica" className="text-muted-foreground hover:text-foreground"><DownloadSimple size={15} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <SafeHtml html={art.contenuto_html} className="kb-content hd-email-body text-slate-700 text-[15px] leading-relaxed" />
                  {isEmptyHtml(art.contenuto_html) && <p className="text-sm text-muted-foreground italic">Nessun contenuto. Clicca Modifica per scrivere l'articolo.</p>}
                </Card>
              </>
            ) : (
              <Card className="rounded-sm shadow-none p-5 space-y-4" data-testid="kb-article-edit">
                <div><Label>Titolo *</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="rounded-sm text-lg font-semibold" data-testid="kb-edit-title" /></div>
                <RichEditor value={form.contenuto_html} onChange={(html) => setForm({ ...form, contenuto_html: html })} onImageUpload={uploadInlineImage} codeBlock testid="kb-editor" placeholder="Scrivi l'articolo… incolla screenshot direttamente, usa i blocchi codice per comandi PowerShell/shell." />
                <div>
                  <DropZone onFiles={(fl) => addAttachments(Array.from(fl))} className="inline-block">
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addAttachments(Array.from(e.target.files || []))} data-testid="kb-att-input" />
                  <Button type="button" variant="outline" size="sm" className="rounded-sm gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="kb-att-add"><Paperclip size={14} /> {uploading ? "Caricamento…" : "Aggiungi allegato"}</Button>
                  <span className="text-[11px] text-muted-foreground ml-2">Max 100 MB per file</span>
                  </DropZone>
                  {atts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {atts.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-sm px-2 py-1 border border-border">
                          <Paperclip size={12} />{a.filename}<button onClick={() => removeAtt(a.id)} className="text-rose-600 hover:text-rose-700" data-testid={`kb-att-remove-${a.id}`}><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-border">
                  <Button onClick={() => save()} className="rounded-sm gap-1.5" data-testid="kb-save-btn"><FloppyDisk size={15} /> Salva</Button>
                  <Button variant="outline" onClick={() => { setSp({}); loadArticle(id); }} className="rounded-sm" data-testid="kb-cancel-btn">Annulla</Button>
                  <Button variant="outline" onClick={del} className="rounded-sm gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50 ml-auto" data-testid="kb-edit-delete"><Trash size={15} /> Elimina</Button>
                </div>
              </Card>
            )}
          </div>

          {/* Pannello proprietà */}
          <div className="w-full lg:w-80 shrink-0">
            <Card className="rounded-sm shadow-none p-4 space-y-3 text-sm" data-testid="kb-properties">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Proprietà articolo</p>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Stato</span>
                {editMode ? (
                  <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })} className="h-8 rounded-sm border border-border bg-white text-sm px-2" data-testid="kb-stato-select">
                    <option value="bozza">Bozza</option><option value="pubblicato">Pubblicato</option><option value="archiviato">Archiviato</option>
                  </select>
                ) : <StatoBadge stato={art.stato} />}
              </div>
              {!editMode && (
                <div className="flex gap-2">
                  {art.stato !== "pubblicato" && <Button size="sm" className="rounded-sm gap-1.5 flex-1" onClick={publish} data-testid="kb-publish-btn"><PaperPlaneTilt size={14} /> Pubblica</Button>}
                  {art.stato !== "archiviato" && <Button size="sm" variant="outline" className="rounded-sm gap-1.5 flex-1" onClick={archive} data-testid="kb-archive-btn"><Archive size={14} /> Archivia</Button>}
                </div>
              )}
              <Row label="Autore" value={art.autore_nome} />
              <Row label="Creato il" value={fmtDateTime(art.created_at)} />
              <Row label="Ultima modifica" value={fmtDateTime(art.updated_at)} />
              <Row label="Modificato da" value={art.last_modified_by} />
              <div className="border-t border-border pt-3 space-y-3">
                <div><Label className="text-muted-foreground">Categoria</Label>
                  {editMode ? (
                    <select value={form.categoria_id} onChange={(e) => { const c = cats.find((x) => x.id === e.target.value); setForm({ ...form, categoria_id: e.target.value, cartella_id: c?.folders?.[0]?.id || "" }); }} className="mt-1 w-full h-9 rounded-sm border border-border bg-white text-sm px-2" data-testid="kb-cat-select">
                      {cats.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  ) : <p className="text-slate-700 font-medium">{art.categoria?.nome}</p>}
                </div>
                <div><Label className="text-muted-foreground">Cartella</Label>
                  {editMode ? (
                    <select value={form.cartella_id} onChange={(e) => setForm({ ...form, cartella_id: e.target.value })} className="mt-1 w-full h-9 rounded-sm border border-border bg-white text-sm px-2" data-testid="kb-folder-select">
                      {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                  ) : <p className="text-slate-700 font-medium">{art.cartella?.nome}</p>}
                </div>
              </div>
              <div className="border-t border-border pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Visibile in area cliente</Label>
                  {editMode ? <Switch checked={form.visibile_area_cliente} onCheckedChange={(v) => setForm({ ...form, visibile_area_cliente: v })} data-testid="kb-visible-switch" /> : <span className="font-medium text-slate-700">{art.visibile_area_cliente ? "Sì" : "No"}</span>}
                </div>
                <div>
                  <Label className="text-muted-foreground">Clienti autorizzati</Label>
                  {editMode ? (
                    <div className="mt-1"><MultiSelect options={clientOptions} value={form.clienti_visibili_ids} onChange={(v) => setForm({ ...form, clienti_visibili_ids: v })} placeholder="Tutti i clienti" width="w-full" searchable testid="kb-clients-select" /></div>
                  ) : (
                    <p className="text-slate-700 mt-0.5">{(art.clienti_visibili_ids || []).length === 0 ? (art.visibile_area_cliente ? "Tutti i clienti" : "—") : (art.clienti_visibili_ids || []).map((cid) => clients.find((c) => c.id === cid)?.ragione_sociale || cid).join(", ")}</p>
                  )}
                  {editMode && <p className="text-[11px] text-muted-foreground mt-1">Vuoto = tutti i clienti (se visibile). Ha effetto solo se "Visibile in area cliente" è attivo.</p>}
                </div>
                <div>
                  <Label className="text-muted-foreground">Tags</Label>
                  {editMode ? <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="separati da virgola" className="rounded-sm mt-1 h-8" data-testid="kb-tags-input" /> : <p className="text-slate-700 mt-0.5">{(art.tags || []).join(", ") || "—"}</p>}
                </div>
              </div>
              {editMode && (
                <div className="border-t border-border pt-3 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">SEO (opzionale)</p>
                  <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="Titolo SEO" className="rounded-sm h-8" />
                  <Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Descrizione SEO" className="rounded-sm h-8" />
                </div>
              )}
            </Card>
            {showHistory && (
              <Card className="rounded-sm shadow-none p-4 mt-4" data-testid="kb-history-panel">
                <div className="flex items-center justify-between mb-2"><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Storico modifiche</p><button onClick={() => setShowHistory(false)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button></div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {history.map((h) => (
                    <div key={h.id} className="text-xs border-l-2 border-border pl-2"><span className="font-medium text-slate-700 capitalize">{h.azione}</span> · {h.utente_nome}<br /><span className="text-muted-foreground">{fmtDateTime(h.created_at)}{h.dettagli ? ` — ${h.dettagli}` : ""}</span></div>
                  ))}
                  {history.length === 0 && <p className="text-xs text-muted-foreground">Nessuna voce.</p>}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </InternalLayout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className="font-medium text-slate-700 text-right truncate max-w-[170px]">{value || "—"}</span></div>
);
